import { useEffect, useRef, useState } from 'react'
/**
 * Download is NEUTRAL, deliberately.
 *
 * These buttons wore --accent-success — green fill, green ring, green glyph —
 * which is this product's word for SELECTED. On a gallery whose entire purpose
 * is for the client to pick photos, that made Download the loudest green thing
 * on screen and left the actual pick competing with it. Green belongs to a
 * chosen photo; one accent per surface, and on this surface it is not this
 * button's.
 */
import { ChevronDown, Download } from 'lucide-react'
import type { ShareDownloadTier } from '../../../src/sharePolicy/clientAccess'
import type { SharePolicyClientDto } from '../../../src/sharePolicy/contracts'
import { buildClientDownloadMenu, type ClientDownloadAvailability } from '../utils/shareDownloads'

export interface PolicyDownloadMenuProps {
  policy: SharePolicyClientDto
  availability: ClientDownloadAvailability
  downloadUrl: string
  onDownload?: (tier: ShareDownloadTier) => void
}

const linkClasses =
  'block rounded-lg px-3 py-2 text-left outline-none hover:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-[var(--text-primary)]'

/** Compact photo action derived only from the normalized client policy. */
export function PolicyDownloadMenu({
  policy,
  availability,
  downloadUrl,
  onDownload,
}: PolicyDownloadMenuProps) {
  const items = buildClientDownloadMenu({ policy, availability, downloadUrl })
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const firstItemRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    if (!open) return
    const closeOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      rootRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
    }
    document.addEventListener('mousedown', closeOutside)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeOutside)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [open])

  useEffect(() => {
    if (open) firstItemRef.current?.focus()
  }, [open])

  if (items.length === 0) return null

  if (items.length === 1) {
    const item = items[0]
    return (
      <a
        data-testid={`policy-download-${item.tier}`}
        aria-label={`Download ${item.label}`}
        href={item.href}
        onClick={() => onDownload?.(item.tier)}
        className="grid h-10 w-10 place-items-center rounded-full border border-[var(--border-default)] bg-[var(--surface-secondary)] text-[var(--text-primary)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-primary)]"
        title={`Download ${item.label}`}
      >
        <Download size={16} strokeWidth={2} aria-hidden />
      </a>
    )
  }

  return (
    <div ref={rootRef} className="relative" data-testid="policy-download-control">
      <button
        type="button"
        aria-label="Download options"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex h-10 items-center gap-1 rounded-full border border-[var(--border-default)] bg-[var(--surface-secondary)] pl-3 pr-2 text-[13px] font-semibold text-[var(--text-primary)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--text-primary)]"
      >
        <Download size={16} strokeWidth={2} aria-hidden />
        <ChevronDown size={14} strokeWidth={2} aria-hidden />
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Available downloads"
          className="absolute right-0 z-50 mt-1.5 w-56 rounded-xl border border-white/[0.12] bg-[var(--surface-elevated,#1b1b1f)] p-1 shadow-2xl"
        >
          {items.map((item, index) => (
            <a
              key={item.tier}
              ref={index === 0 ? firstItemRef : undefined}
              role="menuitem"
              data-testid={`policy-download-${item.tier}`}
              href={item.href}
              onClick={() => {
                onDownload?.(item.tier)
                setOpen(false)
              }}
              className={linkClasses}
            >
              <span className="block text-[13px] font-semibold text-white">{item.label}</span>
              <span className="block text-[11px] text-white/55">{item.description}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  )
}
